/**
 * YUGEN Trust Stats — customer photo carousel
 * Infinite snap scroll + gentle autoplay. No snap-back to the first photo.
 */
(function () {
  'use strict';

  if (customElements.get('yugen-trust-carousel')) return;

  const AUTOPLAY_MS = 3000;
  const SETTLE_MS = 320;

  class YugenTrustCarousel extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.originalSlides = [];
      this.setWidth = 0;
      this.slideStep = 80;
      this.gap = 0;
      this.timer = null;
      this.settling = false;
      this.dragging = false;
      this.reduceMotion = false;

      this.onScroll = this.onScroll.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.tick = this.tick.bind(this);
      this.pause = this.pause.bind(this);
      this.resume = this.resume.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('[data-yts-track]');
      if (!this.track) return;

      this.originalSlides = Array.from(this.track.querySelectorAll('[data-yts-slide]'));
      if (this.originalSlides.length === 0) return;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.setupLoop();
      this.measure();
      this.jumpToMiddle();

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.track.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.track.addEventListener('pointercancel', this.onPointerUp, { passive: true });
      this.track.addEventListener('mouseenter', this.pause);
      this.track.addEventListener('mouseleave', this.resume);
      this.track.addEventListener('focusin', this.pause);
      this.track.addEventListener('focusout', this.resume);
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);

      if (!this.reduceMotion) this.start();
    }

    disconnectedCallback() {
      this.stop();
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('pointerdown', this.onPointerDown);
        this.track.removeEventListener('pointerup', this.onPointerUp);
        this.track.removeEventListener('pointercancel', this.onPointerUp);
        this.track.removeEventListener('mouseenter', this.pause);
        this.track.removeEventListener('mouseleave', this.resume);
        this.track.removeEventListener('focusin', this.pause);
        this.track.removeEventListener('focusout', this.resume);
      }
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.resizeTimer) window.clearTimeout(this.resizeTimer);
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
    }

    setupLoop() {
      if (this.originalSlides.length < 2) return;
      if (this.track.dataset.ytsLooped === 'true') return;

      const fragStart = document.createDocumentFragment();
      const fragEnd = document.createDocumentFragment();

      this.originalSlides.forEach((slide) => {
        const before = slide.cloneNode(true);
        const after = slide.cloneNode(true);
        before.setAttribute('aria-hidden', 'true');
        after.setAttribute('aria-hidden', 'true');
        before.querySelectorAll('img').forEach((img) => {
          img.setAttribute('loading', 'lazy');
          img.removeAttribute('fetchpriority');
        });
        after.querySelectorAll('img').forEach((img) => {
          img.setAttribute('loading', 'lazy');
          img.removeAttribute('fetchpriority');
        });
        fragStart.appendChild(before);
        fragEnd.appendChild(after);
      });

      this.track.insertBefore(fragStart, this.track.firstChild);
      this.track.appendChild(fragEnd);
      this.track.dataset.ytsLooped = 'true';
    }

    measure() {
      const n = this.originalSlides.length;
      if (n < 1) return;

      const slides = this.track.querySelectorAll('[data-yts-slide]');
      const looped = this.track.dataset.ytsLooped === 'true' && n >= 2;
      const start = looped ? n : 0;
      const style = window.getComputedStyle(this.track);
      this.gap = parseFloat(style.columnGap || style.gap) || 0;

      const first = slides[start];
      this.slideStep = (first && first.offsetWidth) || 80;

      let width = 0;
      for (let i = 0; i < n; i += 1) {
        const el = slides[start + i];
        if (!el) continue;
        width += el.offsetWidth;
        if (i < n - 1) width += this.gap;
      }
      this.setWidth = width;
    }

    offsetInSet() {
      if (!this.setWidth) return 0;
      const raw = this.track.scrollLeft % this.setWidth;
      return raw < 0 ? raw + this.setWidth : raw;
    }

    jumpToMiddle() {
      if (!this.setWidth || this.originalSlides.length < 2) return;
      this.scrollQuiet(this.setWidth + this.offsetInSet());
    }

    scrollQuiet(left) {
      this.settling = true;
      this.track.scrollLeft = left;
      requestAnimationFrame(() => {
        this.settling = false;
      });
    }

    normalizeLoop() {
      if (!this.setWidth || this.originalSlides.length < 2 || this.settling) return;

      const x = this.track.scrollLeft;
      if (x < this.setWidth) {
        this.scrollQuiet(x + this.setWidth);
      } else if (x >= this.setWidth * 2) {
        this.scrollQuiet(x - this.setWidth);
      }
    }

    onScroll() {
      if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => this.normalizeLoop());
    }

    onPointerDown() {
      this.dragging = true;
      this.pause();
    }

    onPointerUp() {
      this.dragging = false;
      window.setTimeout(() => this.resume(), SETTLE_MS);
    }

    onResize() {
      if (this.resizeTimer) window.clearTimeout(this.resizeTimer);
      const oldSet = this.setWidth;
      const ratio = oldSet ? this.offsetInSet() / oldSet : 0;
      this.resizeTimer = window.setTimeout(() => {
        this.measure();
        if (!this.setWidth || this.originalSlides.length < 2) return;
        this.scrollQuiet(this.setWidth + ratio * this.setWidth);
      }, 120);
    }

    onVisibility() {
      if (document.hidden) this.pause();
      else this.resume();
    }

    start() {
      this.stop();
      if (this.reduceMotion || this.originalSlides.length < 2) return;
      this.timer = window.setInterval(this.tick, AUTOPLAY_MS);
    }

    stop() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    pause() {
      this.stop();
    }

    resume() {
      if (this.dragging || document.hidden || this.reduceMotion) return;
      this.start();
    }

    tick() {
      if (!this.track || this.dragging || this.settling) return;
      this.track.scrollBy({ left: this.slideStep + this.gap, behavior: 'smooth' });
    }
  }

  customElements.define('yugen-trust-carousel', YugenTrustCarousel);
})();
